import { InlineTextInput } from '@cint/wonderment';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { getPanelCurrencyAmount } from '@/components/wonderment/utils';
import { PanelistCurrency } from '@/utils/types';

export interface FormCurrencyAmountFieldProps {
  name: string;
  testId?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  currencyDisplayDecimals?: number;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: ChangeEvent<HTMLInputElement>) => void;
  isPanelCurrency?: boolean;
  panelistCurrencyAmount?: number;
  panelistCurrency?: PanelistCurrency | null;
  width?: string | number;
}

interface GenericFormValues {
  [key: string]: any;
}

const toNumber = (val: unknown) => {
  if (val === '' || val === null || val === undefined) return undefined;
  const n = typeof val === 'number' ? val : Number(val);
  return Number.isFinite(n) ? n : undefined;
};

const formatFixed = (val: number, decimals = 0) => val.toFixed(decimals);

export const CurrencyField = (props: FormCurrencyAmountFieldProps) => {
  const { control } = useFormContext<GenericFormValues>();
  return (
    <Controller
      control={control}
      name={props.name}
      render={(fieldProps) => (
        <InnerCurrencyField
          {...props}
          field={fieldProps.field}
          isDisabled={props.isDisabled}
        />
      )}
    />
  );
};

function InnerCurrencyField({
  field,
  width = 68,
  testId,
  isDisabled,
  isRequired,
  currencyDisplayDecimals,
  onChange,
  onBlur,
  isPanelCurrency,
  panelistCurrencyAmount,
  panelistCurrency,
}: FormCurrencyAmountFieldProps & { field: any }) {
  const displayDecimals = currencyDisplayDecimals ?? 0;
  const isFocusedRef = useRef(false);

  const initialValue = (() => {
    if (isPanelCurrency && panelistCurrencyAmount !== undefined) {
      const converted = getPanelCurrencyAmount({
        panelistCurrencyAmount,
        currencyMinorUnits: displayDecimals,
        panelistCurrency,
      })?.display;
      const num = toNumber(converted);
      return num !== undefined ? formatFixed(num, displayDecimals) : '';
    }
    const num = toNumber(field.value);
    return num !== undefined ? formatFixed(num, displayDecimals) : '';
  })();

  const [displayValue, setDisplayValue] = useState(initialValue);

  useEffect(() => {
    if (isFocusedRef.current) return;
    const num = toNumber(field.value);
    if (num !== undefined) {
      setDisplayValue(formatFixed(num, displayDecimals));
    } else {
      setDisplayValue('');
    }
  }, [field.value, displayDecimals]);

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  const handleBlur = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      isFocusedRef.current = false;
      onBlur?.(event);

      const num = toNumber(event.target.value);
      if (num === undefined) {
        setDisplayValue('');
        field.onChange(undefined);
        field.onBlur();
        return;
      }

      const formatted = formatFixed(num, displayDecimals);
      if (formatted === displayValue) {
        field.onBlur();
        return;
      }
      setDisplayValue(formatted);
      field.onChange(formatted);
      field.onBlur();
    },
    [displayDecimals, displayValue, field, onBlur]
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      setDisplayValue(raw);
      onChange?.(event);

      const num = toNumber(raw);
      if (num !== undefined) {
        field.onChange(num);
      }
    },
    [field, onChange]
  );

  return (
    <InlineTextInput
      inputSize="sm"
      style={{ width }}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      isDisabled={isDisabled}
      data-testid={testId}
      required={isRequired}
    />
  );
}
